import { RestaurantCallButton } from "@taunggyi-plus/taxi-feature";
import { useDetailRecord } from "@taunggyi-plus/auth";
import { RecordFormDialog } from "@taunggyi-plus/design-system";
import { RecordTable } from "@taunggyi-plus/design-system";
import { useOrderRefresh } from "@taunggyi-plus/auth";
import { useFeedback } from "@taunggyi-plus/auth";
import { CountStrip, ProviderReputation } from "@taunggyi-plus/finance-feature";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  ManagementListToolbar,
  PaginatedList,
  StatusPill,
} from "@taunggyi-plus/design-system";
import type { Locale } from "@taunggyi-plus/domain";
type Item = {
  id: string;
  shop_id: string;
  name: string;
  description: string;
  price_minor: number;
  is_available: boolean;
};
type Shop = {
  id: string;
  organization_id: string;
  display_name: string;
  description: string;
  address: string;
  phone: string;
  is_open: boolean;
  is_published: boolean;
  merchant_items?: Item[];
};

type OrderItem = {
  order_id: string;
  item_id: string;
  item_name: string;
  quantity: number;
  line_total_minor: number;
};
type Order = {
  id: string;
  status: string;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  total_minor: number;
  created_at: string;
};
type CustomerOrder = Order & {
  delivery_status: string | null;
  delivery_requested: boolean;
};
type DeliveryReference = { merchant_order_id: string; status: string };
const labels = {
  en: {
    browse: "Link Food",
    browseHelp: "Order from approved Taunggyi merchants and pay cash on delivery.",
    empty: "No published shops yet.",
    order: "Order this item",
    name: "Name",
    phone: "Phone",
    address: "Delivery address",
    note: "Note",
    send: "Place cash-on-delivery order",
    sent: "Order submitted. The merchant was notified.",
    signin: "Sign in before ordering.",
    myOrders: "My food orders",
    myOrdersHelp: "Track merchant confirmation and delivery progress in one place.",
    noCustomerOrders: "You have no food orders yet.",
    deliveryWaiting: "Waiting for delivery assignment",
    delivery: "Delivery",
    orderReference: "Order",
    cancelOrder: "Cancel order",
    cancellationSaved: "Order cancelled. The merchant was notified.",
    manage: "Shop and order management",
    setup: "Create shop profile",
    shop: "Shop name",
    description: "Description",
    open: "Open for orders",
    save: "Create shop",
    catalog: "Add catalog item",
    item: "Item name",
    price: "Price (MMK)",
    add: "Add item",
    orders: "Order queue",
    none: "No orders yet",
    accepted: "Accept",
    rejected: "Reject",
    preparing: "Start preparing",
    ready: "Ready",
    completed: "Complete",
    saved: "Saved successfully.",
    unpublished: "Waiting for Admin publishing approval",
  },
  my: {
    browse: "အစားအသောက်နှင့် ဒေသခံဆိုင်များ",
    browseHelp: "တောင်ကြီးရှိ အတည်ပြုဆိုင်များမှ မှာယူပြီး ပို့ဆောင်ချိန်တွင် ငွေပေးပါ။",
    empty: "ထုတ်ပြန်ထားသောဆိုင် မရှိသေးပါ။",
    order: "ဤပစ္စည်းကို မှာမည်",
    name: "အမည်",
    phone: "ဖုန်း",
    address: "ပို့ဆောင်ရန်လိပ်စာ",
    note: "မှတ်ချက်",
    send: "ငွေချေမှာယူမည်",
    sent: "မှာယူမှု ပို့ပြီး ဆိုင်ကို အသိပေးပြီးပါပြီ။",
    signin: "မှာယူမီ အကောင့်ဝင်ပါ။",
    myOrders: "ကျွန်ုပ်၏ အစားအသောက်မှာယူမှုများ",
    myOrdersHelp: "ဆိုင်အတည်ပြုမှုနှင့် ပို့ဆောင်မှုအခြေအနေကို တစ်နေရာတည်းတွင် ကြည့်ပါ။",
    noCustomerOrders: "အစားအသောက်မှာယူမှု မရှိသေးပါ။",
    deliveryWaiting: "ပို့ဆောင်သူ သတ်မှတ်ရန် စောင့်နေသည်",
    delivery: "ပို့ဆောင်မှု",
    orderReference: "မှာယူမှု",
    cancelOrder: "မှာယူမှု ပယ်ဖျက်မည်",
    cancellationSaved: "မှာယူမှု ပယ်ဖျက်ပြီး ဆိုင်ကို အသိပေးပြီးပါပြီ။",
    manage: "ဆိုင်နှင့် မှာယူမှု စီမံခန့်ခွဲမှု",
    setup: "ဆိုင်အချက်အလက် ဖန်တီးရန်",
    shop: "ဆိုင်အမည်",
    description: "ဖော်ပြချက်",
    open: "မှာယူနိုင်ရန် ဖွင့်မည်",
    save: "ဆိုင်ဖန်တီးမည်",
    catalog: "ပစ္စည်းထည့်ရန်",
    item: "ပစ္စည်းအမည်",
    price: "စျေးနှုန်း (MMK)",
    add: "ပစ္စည်းထည့်မည်",
    orders: "မှာယူမှုစာရင်း",
    none: "မှာယူမှု မရှိသေးပါ",
    accepted: "လက်ခံမည်",
    rejected: "ငြင်းမည်",
    preparing: "ပြင်ဆင်မည်",
    ready: "အဆင်သင့်",
    completed: "ပြီးဆုံး",
    saved: "သိမ်းပြီးပါပြီ။",
    unpublished: "Admin ထုတ်ပြန်ခွင့်ကို စောင့်နေသည်",
  },
  shn: {
    browse: "တၢင်းၵိၼ်လႄႈႁၢၼ်ႉ",
    browseHelp: "သင်ႇတီႈႁၢၼ်ႉၼႂ်းတူၼ်ႈတီးလႄႈပၼ်ငိုၼ်းမိူဝ်ႈသူင်ႇ။",
    empty: "ပႆႇမီးႁၢၼ်ႉ။",
    order: "သင်ႇၶူဝ်းၼႆႉ",
    name: "ၸိုဝ်ႈ",
    phone: "ၾူၼ်း",
    address: "တီႈယူႇသူင်ႇ",
    note: "မၢႆတွင်း",
    send: "သူင်ႇၶေႃႈသင်ႇ",
    sent: "သူင်ႇၶေႃႈသင်ႇယဝ်ႉ။",
    signin: "ၶဝ်ႈဢၶွင်ႉၵွၼ်ႇသင်ႇ။",
    myOrders: "ၶေႃႈသင်ႇတၢင်းၵိၼ်ၶွင်ၶႃႈ",
    myOrdersHelp: "တူၺ်းၵၢၼ်ယိုၼ်ယၼ်လႄႈၵၢၼ်သူင်ႇတီႈလဵဝ်။",
    noCustomerOrders: "ပႆႇမီးၶေႃႈသင်ႇတၢင်းၵိၼ်။",
    deliveryWaiting: "ပႂ်ႉၸတ်းၽူႈသူင်ႇ",
    delivery: "ၵၢၼ်သူင်ႇ",
    orderReference: "ၶေႃႈသင်ႇ",
    cancelOrder: "ယႃႉၶေႃႈသင်ႇ",
    cancellationSaved: "ယႃႉၶေႃႈသင်ႇလႄႈပၼ်ၶၢဝ်ႇႁၢၼ်ႉယဝ်ႉ။",
    manage: "ၸတ်းၵၢၼ်ႁၢၼ်ႉ",
    setup: "သၢင်ႈႁၢၼ်ႉ",
    shop: "ၸိုဝ်ႈႁၢၼ်ႉ",
    description: "ၶေႃႈသပ်းလႅင်း",
    open: "ပိုတ်ႇႁပ်ႉ order",
    save: "သၢင်ႈႁၢၼ်ႉ",
    catalog: "သႂ်ႇၶူဝ်း",
    item: "ၸိုဝ်ႈၶူဝ်း",
    price: "ၵႃႈၶၼ်",
    add: "သႂ်ႇၶူဝ်း",
    orders: "သဵၼ်ႈ order",
    none: "ပႆႇမီး order",
    accepted: "ႁပ်ႉ",
    rejected: "ပၢၵ်ႈ",
    preparing: "ႁၢင်ႈႁႅၼ်း",
    ready: "ယဝ်ႉ",
    completed: "သုတ်း",
    saved: "သိမ်းယဝ်ႉ။",
    unpublished: "ပႂ်ႉ Admin ယိုၼ်ယၼ်",
  },
  zh: {
    browse: "美食与本地商店",
    browseHelp: "从东枝认证商户下单，货到付款。",
    empty: "暂无已发布商店。",
    order: "订购此商品",
    name: "姓名",
    phone: "电话",
    address: "配送地址",
    note: "备注",
    send: "提交货到付款订单",
    sent: "订单已提交，商户已收到通知。",
    signin: "下单前请先登录。",
    myOrders: "我的餐饮订单",
    myOrdersHelp: "在一个页面查看商户确认和配送进度。",
    noCustomerOrders: "您还没有餐饮订单。",
    deliveryWaiting: "等待分配配送员",
    delivery: "配送",
    orderReference: "订单",
    cancelOrder: "取消订单",
    cancellationSaved: "订单已取消，并已通知商户。",
    manage: "商店与订单管理",
    setup: "创建商店资料",
    shop: "商店名称",
    description: "说明",
    open: "开放接单",
    save: "创建商店",
    catalog: "添加商品",
    item: "商品名称",
    price: "价格（MMK）",
    add: "添加商品",
    orders: "订单队列",
    none: "暂无订单",
    accepted: "接受",
    rejected: "拒绝",
    preparing: "开始准备",
    ready: "已备好",
    completed: "完成",
    saved: "保存成功。",
    unpublished: "等待管理员发布批准",
  },
} as const;

const statusLabels: Record<Locale, Record<string, string>> = {
  en: {
    pending: "Pending",
    accepted: "Accepted",
    preparing: "Preparing",
    ready: "Ready for delivery",
    rejected: "Rejected",
    cancelled: "Cancelled",
    completed: "Completed",
    unassigned: "Unassigned",
    assigned: "Rider assigned",
    picked_up: "Picked up",
    delivered: "Delivered",
    failed: "Delivery failed",
  },
  my: {
    pending: "စောင့်ဆိုင်းဆဲ",
    accepted: "လက်ခံပြီး",
    preparing: "ပြင်ဆင်နေသည်",
    ready: "ပို့ဆောင်ရန် အဆင်သင့်",
    rejected: "ငြင်းပယ်ပြီး",
    cancelled: "ပယ်ဖျက်ပြီး",
    completed: "ပြီးဆုံး",
    unassigned: "ပို့ဆောင်သူ မသတ်မှတ်ရသေး",
    assigned: "ပို့ဆောင်သူ သတ်မှတ်ပြီး",
    picked_up: "ပစ္စည်းယူပြီး",
    delivered: "ပို့ဆောင်ပြီး",
    failed: "ပို့ဆောင်မှု မအောင်မြင်",
  },
  shn: {
    pending: "တိုၵ်ႉပႂ်ႉ",
    accepted: "ႁပ်ႉယဝ်ႉ",
    preparing: "တိုၵ်ႉႁၢင်ႈႁႅၼ်း",
    ready: "တူဝ်ႈတၼ်းသူင်ႇ",
    rejected: "ပၢၵ်ႈယဝ်ႉ",
    cancelled: "ယႃႉယဝ်ႉ",
    completed: "သုတ်းယဝ်ႉ",
    unassigned: "ပႆႇၸတ်းၽူႈသူင်ႇ",
    assigned: "ၸတ်းၽူႈသူင်ႇယဝ်ႉ",
    picked_up: "ႁပ်ႉၶူဝ်းယဝ်ႉ",
    delivered: "သူင်ႇယဝ်ႉ",
    failed: "သူင်ႇဢမ်ႇပဵၼ်",
  },
  zh: {
    pending: "待处理",
    accepted: "已接受",
    preparing: "准备中",
    ready: "待配送",
    rejected: "已拒绝",
    cancelled: "已取消",
    completed: "已完成",
    unassigned: "未分配",
    assigned: "已分配骑手",
    picked_up: "已取货",
    delivered: "已送达",
    failed: "配送失败",
  },
};

export function CustomerMerchantOrdering({
  locale,
  renderAuthPrompt,
  actionLabel,
  variant = "directory",
  limit,
  searchQuery = "",
  onResultCount,
}: {
  locale: Locale;
  renderAuthPrompt?: (onClose: () => void) => ReactNode;
  actionLabel?: string;
  variant?: "directory" | "home";
  limit?: number;
  searchQuery?: string;
  onResultCount?: (count: number) => void;
}) {
  const auth = useAuth(),
    t = labels[locale];
  const [shops, setShops] = useState<Shop[]>([]),
    [selected, setSelected] = useDetailRecord(shops.flatMap((shop) => shop.merchant_items ?? [])),
    [message, setMessage] = useFeedback(),
    [historyVersion, setHistoryVersion] = useState(0),
    [actionStarted, setActionStarted] = useState(false),
    [loginRequested, setLoginRequested] = useState(false);
  useEffect(() => {
    if (!auth.client) return;
    let active = true;
    void Promise.all([
      auth.client
        .from("merchant_shops")
        .select("id,organization_id,display_name,description,address,phone,is_open,is_published")
        .eq("is_open", true)
        .eq("is_published", true),
      auth.client
        .from("merchant_items")
        .select("id,shop_id,name,description,price_minor,is_available")
        .eq("is_available", true),
    ]).then(([shopResult, itemResult]) => {
      const shopRows = (shopResult.data ?? []) as Omit<Shop, "merchant_items">[];
      const itemRows = (itemResult.data ?? []) as Item[];
      const next = shopRows.map((shop) => ({
        ...shop,
        merchant_items: itemRows.filter((item) => item.shop_id === shop.id),
      }));
      if (active) setShops(next);
    });
    return () => {
      active = false;
    };
  }, [auth.client]);
  const showOrderForm = actionStarted || Boolean(auth.user && loginRequested);
  const catalog = shops.flatMap((shop) =>
    (shop.merchant_items ?? []).filter((item) => item.is_available).map((item) => ({ item, shop })),
  );
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const matchingCatalog = normalizedSearch
    ? catalog.filter(({ item, shop }) =>
        [item.name, item.description, shop.display_name, shop.description, shop.address]
          .join(" ")
          .toLocaleLowerCase()
          .includes(normalizedSearch),
      )
    : catalog;
  const visibleCatalog =
    typeof limit === "number" ? matchingCatalog.slice(0, limit) : matchingCatalog;
  useEffect(() => onResultCount?.(matchingCatalog.length), [matchingCatalog.length, onResultCount]);
  const checkout = useRef({ item: "", id: "" });
  const submitting = useRef(false);
  const selectedShop = selected ? shops.find((shop) => shop.id === selected.shop_id) : null;
  if (normalizedSearch && !matchingCatalog.length) return null;
  async function order(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!auth.client || !auth.user || !selected) return setMessage(t.signin);
    if (submitting.current) return;
    submitting.current = true;
    const form = e.currentTarget;
    const d = new FormData(form);
    if (checkout.current.item !== selected.id)
      checkout.current = { item: selected.id, id: crypto.randomUUID() };
    try {
      const { error } = await auth.client.rpc("create_merchant_order", {
        request_id: checkout.current.id,
        target_shop_id: selected.shop_id,
        customer_name: d.get("name"),
        customer_phone: d.get("phone"),
        delivery_address: d.get("address"),
        customer_note: d.get("note"),
        requested_items: [{ item_id: selected.id, quantity: 1 }],
      });
      setMessage(error?.message ?? t.sent);
      if (!error) {
        form.reset();
        checkout.current = { item: "", id: "" };
        setSelected(null);
        setHistoryVersion((value) => value + 1);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not submit. Retry this request.");
    } finally {
      submitting.current = false;
    }
  }
  return (
    <section className={`merchant-feature ${variant === "home" ? "provider-home-directory" : ""}`}>
      {variant === "directory" && (
        <div className="section-heading">
          <div>
            <h2>{t.browse}</h2>
          </div>
        </div>
      )}
      <div className="hotel-directory-grid" hidden={Boolean(selected)}>
        {visibleCatalog.length ? (
          <PaginatedList
            layout="cards"
            items={visibleCatalog}
            resetKey={normalizedSearch}
            label="Food catalog pages"
            renderItem={({ item, shop }, index) => (
              <button
                className="hotel-listing-card"
                key={item.id}
                type="button"
                aria-haspopup="dialog"
                onClick={() => {
                  setSelected(item);
                  setActionStarted(false);
                  setLoginRequested(false);
                  setMessage("");
                }}
              >
                <MerchantArtwork variant={index % 3} />
                <span className="hotel-listing-card__body">
                  <strong>{item.name}</strong>
                  <small>{shop.display_name}</small>
                  <span>{item.description}</span>
                  <b>{(item.price_minor / 100).toLocaleString()} MMK</b>
                  <em>
                    {t.order} <span aria-hidden="true">→</span>
                  </em>
                </span>
              </button>
            )}
          />
        ) : normalizedSearch ? null : (
          <Card className="empty-state">
            <strong>{t.empty}</strong>
          </Card>
        )}
      </div>
      {selected && (
        <div className="provider-detail-page" role="presentation">
          <section
            className="provider-detail-content"
            role="region"
            aria-labelledby="merchant-detail-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="hotel-detail-dialog__header">
              <div>
                <p className="eyebrow">{t.browse}</p>
                <h2 id="merchant-detail-title">{selected.name}</h2>
              </div>
              <button
                className="service-dialog__close"
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="hotel-detail-dialog__content">
              <div className="hotel-detail-hero">
                <MerchantArtwork
                  variant={
                    Math.max(
                      0,
                      catalog.findIndex(({ item }) => item.id === selected.id),
                    ) % 3
                  }
                />
                <div>
                  <p className="eyebrow">{selectedShop?.display_name}</p>
                  {selectedShop && <RestaurantCallButton shopId={selectedShop.id} />}
                  <p>{selected.description}</p>
                  <strong>{(selected.price_minor / 100).toLocaleString()} MMK</strong>
                </div>
              </div>
              {selectedShop && (
                <ProviderReputation
                  key={selectedShop.organization_id}
                  org={selectedShop.organization_id}
                />
              )}
              <Card className="merchant-order-card">
                <h3>{t.order}</h3>

                {!showOrderForm && !loginRequested && (
                  <button
                    className="button merchant-primary-action"
                    type="button"
                    onClick={() => (auth.user ? setActionStarted(true) : setLoginRequested(true))}
                  >
                    {actionLabel ?? t.order}
                  </button>
                )}
                {loginRequested && !auth.user && renderAuthPrompt?.(() => setLoginRequested(false))}
                {showOrderForm && (
                  <form className="form-grid form-grid--two" onSubmit={(e) => void order(e)}>
                    <label className="field">
                      <span>{t.name}</span>
                      <input name="name" required />
                    </label>
                    <label className="field">
                      <span>{t.phone}</span>
                      <input name="phone" required />
                    </label>
                    <label className="field field--wide">
                      <span>{t.address}</span>
                      <textarea name="address" required />
                    </label>
                    <label className="field field--wide">
                      <span>{t.note}</span>
                      <textarea name="note" />
                    </label>
                    <button className="button field--wide">{t.send}</button>
                  </form>
                )}
                {message && <p className="form-message">{message}</p>}
              </Card>
              {auth.user && <CustomerOrderHistory locale={locale} version={historyVersion} />}
            </div>
          </section>
        </div>
      )}
      {variant === "directory" && auth.user && !selected && (
        <CustomerOrderHistory locale={locale} version={historyVersion} />
      )}
    </section>
  );
}

function MerchantArtwork({ variant }: { readonly variant: number }) {
  return (
    <span
      className={`hotel-listing-card__image provider-art provider-art--food hotel-listing-card__image--${variant}`}
      aria-hidden="true"
    >
      <span />
      <svg viewBox="0 0 80 64">
        <path d="M25 9v20M17 9v12c0 6 4 9 8 9s8-3 8-9V9M25 30v25M48 55V12c10 2 15 10 15 20H48" />
      </svg>
      <i />
    </span>
  );
}

export function CustomerOrderHistory({
  locale,
  version = 0,
}: {
  locale: Locale;
  version?: number;
}) {
  const auth = useAuth();
  const t = labels[locale];
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [message, setMessage] = useFeedback();
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    if (!auth.client || !auth.user) return;
    let active = true;
    void Promise.all([
      auth.client
        .from("merchant_orders")
        .select("id,status,customer_name,customer_phone,delivery_address,total_minor,created_at")
        .order("created_at", { ascending: false }),
      auth.client.from("delivery_requests").select("merchant_order_id,status"),
      auth.client.from("delivery_jobs").select("merchant_order_id,status"),
    ]).then(([orderResult, requestResult, jobResult]) => {
      if (!active) return;
      const error = orderResult.error ?? requestResult.error ?? jobResult.error;
      if (error) {
        setMessage(error.message);
        return;
      }
      const requests = new Set(
        ((requestResult.data ?? []) as DeliveryReference[]).map(
          (request) => request.merchant_order_id,
        ),
      );
      const deliveryStatuses = new Map(
        ((jobResult.data ?? []) as DeliveryReference[]).map((job) => [
          job.merchant_order_id,
          job.status,
        ]),
      );
      setOrders(
        ((orderResult.data ?? []) as Order[]).map((order) => ({
          ...order,
          delivery_requested: requests.has(order.id),
          delivery_status: deliveryStatuses.get(order.id) ?? null,
        })),
      );
    });
    return () => {
      active = false;
    };
  }, [auth.client, auth.user, setMessage, version]);

  async function cancel(id: string) {
    if (!auth.client) return;
    setBusyId(id);
    setMessage("");
    const { error } = await auth.client.rpc("cancel_merchant_order", { order_id: id });
    setBusyId("");
    if (error) {
      setMessage(error.message);
      return;
    }
    setOrders((current) =>
      current.map((order) => (order.id === id ? { ...order, status: "cancelled" } : order)),
    );
    setMessage(t.cancellationSaved);
  }

  if (!auth.user) return null;
  return (
    <div className="customer-order-history">
      <div className="section-heading">
        <div>
          <h2>{t.myOrders}</h2>
        </div>
      </div>
      {message && <p className="form-message">{message}</p>}
      <div className="request-list">
        {orders.length ? (
          <PaginatedList
            items={orders}
            resetKey={String(orders.length)}
            label="Customer order pages"
            renderItem={(order) => (
              <Card className="request-card customer-order" key={order.id}>
                <div>
                  <StatusPill
                    tone={
                      order.status === "completed"
                        ? "success"
                        : ["rejected", "cancelled"].includes(order.status)
                          ? "neutral"
                          : "warning"
                    }
                  >
                    {statusLabels[locale][order.status] ?? order.status}
                  </StatusPill>
                  <h3>
                    {t.orderReference} #{order.id.slice(0, 8).toUpperCase()}
                  </h3>
                  <p>
                    {new Date(order.created_at).toLocaleDateString(
                      locale === "en" ? "en-GB" : locale === "zh" ? "zh-CN" : `${locale}-MM`,
                    )}
                    {" · "}
                    {(order.total_minor / 100).toLocaleString()} MMK
                  </p>
                  <p>{order.delivery_address}</p>
                </div>
                {order.delivery_status ? (
                  <div className="delivery-progress">
                    <small>{t.delivery}</small>
                    <strong>
                      {statusLabels[locale][order.delivery_status] ?? order.delivery_status}
                    </strong>
                  </div>
                ) : order.delivery_requested ? (
                  <div className="delivery-progress">
                    <small>{t.delivery}</small>
                    <strong>{t.deliveryWaiting}</strong>
                  </div>
                ) : order.status === "pending" ? (
                  <button
                    className="button button--danger"
                    disabled={busyId === order.id}
                    onClick={() => void cancel(order.id)}
                  >
                    {busyId === order.id ? "…" : t.cancelOrder}
                  </button>
                ) : null}
              </Card>
            )}
          />
        ) : (
          <Card className="empty-state">
            <span className="empty-state__icon">⌁</span>
            <strong>{t.noCustomerOrders}</strong>
          </Card>
        )}
      </div>
    </div>
  );
}

export function MerchantConsole({
  locale,
  view = "dashboard",
}: {
  locale: Locale;
  readonly view?: "dashboard" | "foods" | "orders";
}) {
  const auth = useAuth(),
    t = labels[locale];
  const [org, setOrg] = useState<string | null>(null),
    [shop, setShop] = useState<Shop | null>(null),
    [orders, setOrders] = useState<Order[]>([]),
    [message, setMessage] = useFeedback();
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [itemQuery, setItemQuery] = useState("");
  const [itemStatus, setItemStatus] = useState("all");
  const [orderQuery, setOrderQuery] = useState("");
  const [orderStatus, setOrderStatus] = useState("all");
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const { data: m } = await auth.client
      .from("organization_memberships")
      .select("organization_id")
      .eq("user_id", auth.user.id)
      .eq("role", "merchant_owner")
      .eq("status", "active")
      .limit(1)
      .maybeSingle();
    if (!m) return;
    setOrg(m.organization_id);
    const { data: s } = await auth.client
      .from("merchant_shops")
      .select("id,organization_id,display_name,description,address,phone,is_open,is_published")
      .eq("organization_id", m.organization_id)
      .maybeSingle();
    setShop((s as Shop | null) ?? null);
    if (s) {
      const { data: o } = await auth.client
        .from("merchant_orders")
        .select("id,status,customer_name,customer_phone,delivery_address,total_minor,created_at")
        .eq("shop_id", s.id)
        .order("created_at", { ascending: false });
      setOrders((o ?? []) as Order[]);
      if (o?.length) {
        const result = await auth.client
          .from("merchant_order_items")
          .select("order_id,item_id,item_name,quantity,line_total_minor")
          .in(
            "order_id",
            (o as Order[]).map((order) => order.id),
          );
        if (result.error) setMessage(result.error.message);
        else setOrderItems((result.data ?? []) as OrderItem[]);
      } else setOrderItems([]);
      const itemResult = await auth.client
        .from("merchant_items")
        .select("id,shop_id,name,description,price_minor,is_available")
        .eq("shop_id", s.id)
        .order("name");
      if (itemResult.error) setMessage(itemResult.error.message);
      else setItems((itemResult.data ?? []) as Item[]);
    }
  }, [auth.client, auth.user, setMessage]);
  useOrderRefresh(load);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  async function createShop(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!auth.client || !org) return;
    const d = new FormData(e.currentTarget);
    const { error } = await auth.client.from("merchant_shops").insert({
      organization_id: org,
      display_name: d.get("name"),
      description: d.get("description"),
      address: d.get("address"),
      phone: d.get("phone"),
      is_open: true,
    });
    setMessage(error?.message ?? t.saved);
    if (!error) await load();
  }
  async function addItem(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    if (!auth.client || !shop) return;
    const d = new FormData(form);
    const { error } = await auth.client.from("merchant_items").insert({
      shop_id: shop.id,
      name: d.get("name"),
      description: d.get("description"),
      price_minor: Math.round(Number(d.get("price")) * 100),
      currency: "MMK",
      is_available: true,
    });
    setMessage(error?.message ?? t.saved);
    if (!error) {
      form.reset();
      form.closest("dialog")?.close();
      await load();
    }
  }
  async function next(id: string, status: string) {
    if (!auth.client) return;
    const { error } = await auth.client.rpc("update_merchant_order_status", {
      order_id: id,
      next_status: status,
      note: "",
    });
    setMessage(error?.message ?? t.saved);
    if (!error) await load();
  }
  const filteredItems = items.filter(
    (item) =>
      (itemStatus === "all" || item.is_available === (itemStatus === "available")) &&
      `${item.name} ${item.description}`.toLowerCase().includes(itemQuery.trim().toLowerCase()),
  );
  const filteredOrders = useMemo(() => {
    const normalized = orderQuery.trim().toLocaleLowerCase();
    return orders.filter(
      (order) =>
        (orderStatus === "all" || order.status === orderStatus) &&
        (!normalized ||
          [order.customer_name, order.customer_phone, order.delivery_address].some((value) =>
            value.toLocaleLowerCase().includes(normalized),
          )),
    );
  }, [orderQuery, orderStatus, orders]);
  if (!auth.user) return null;
  return (
    <section className="merchant-feature">
      {view === "foods" && (
        <CountStrip
          values={[
            ["Active products", items.filter((i) => i.is_available).length],
            ["Total products", items.length],
          ]}
        />
      )}
      {view !== "orders" && !shop && org && (
        <RecordFormDialog title={t.setup}>
          <form className="form-grid" onSubmit={(e) => void createShop(e)}>
            {["name", "description", "address", "phone"].map((k) => (
              <label className="field" key={k}>
                <span>
                  {k === "name"
                    ? t.shop
                    : k === "description"
                      ? t.description
                      : k === "address"
                        ? t.address
                        : t.phone}
                </span>
                <input name={k} required />
              </label>
            ))}
            <button className="button">{t.save}</button>
          </form>
        </RecordFormDialog>
      )}
      {shop && (
        <>
          <p className="workspace-context">
            {shop.is_published ? shop.display_name : t.unpublished}
          </p>
          {view === "foods" && (
            <section>
              <h3>Food list</h3>
              <ManagementListToolbar
                actions={
                  <RecordFormDialog title={t.catalog}>
                    <form className="form-grid form-grid--two" onSubmit={(e) => void addItem(e)}>
                      <label className="field">
                        <span>{t.item}</span>
                        <input name="name" required />
                      </label>
                      <label className="field">
                        <span>{t.price}</span>
                        <input name="price" type="number" required />
                      </label>
                      <label className="field field--wide">
                        <span>{t.description}</span>
                        <textarea name="description" />
                      </label>
                      <button className="button field--wide">{t.add}</button>
                    </form>
                  </RecordFormDialog>
                }
                query={itemQuery}
                onQueryChange={setItemQuery}
                searchLabel="Search foods"
                resultCount={filteredItems.length}
                filters={[
                  {
                    label: "Status",
                    value: itemStatus,
                    onChange: setItemStatus,
                    options: [
                      { value: "all", label: "All statuses" },
                      { value: "available", label: "Available" },
                      { value: "unavailable", label: "Unavailable" },
                    ],
                  },
                ]}
                onClear={() => {
                  setItemQuery("");
                  setItemStatus("all");
                }}
              />
              <div className="filtered-list-stack">
                <RecordTable
                  items={filteredItems}
                  rowKey={(item) => item.id}
                  resetKey={itemQuery + itemStatus}
                  label="Foods"
                  columns={[
                    {
                      key: "name",
                      label: "Food",
                      render: (item) => (
                        <>
                          <strong>{item.name}</strong>
                          <div className="record-table__secondary">{item.description}</div>
                        </>
                      ),
                    },
                    {
                      key: "price",
                      label: "Price",
                      render: (item) => <>{(item.price_minor / 100).toLocaleString()} MMK</>,
                    },
                    {
                      key: "status",
                      label: "Status",
                      render: (item) => (
                        <StatusPill tone={item.is_available ? "success" : "neutral"}>
                          {item.is_available ? "Available" : "Unavailable"}
                        </StatusPill>
                      ),
                    },
                  ]}
                />
              </div>
              {!filteredItems.length && <p>No foods match these filters.</p>}
            </section>
          )}
          {view !== "foods" && (
            <>
              <h3 className="queue-title">{t.orders}</h3>
              <ManagementListToolbar
                query={orderQuery}
                onQueryChange={setOrderQuery}
                searchLabel="Search customer, phone, or address"
                resultCount={filteredOrders.length}
                filters={[
                  {
                    label: "Status",
                    value: orderStatus,
                    onChange: setOrderStatus,
                    options: [
                      { value: "all", label: "All statuses" },
                      ...Array.from(new Set(orders.map((order) => order.status))).map((value) => ({
                        value,
                        label: value in t ? String(t[value as keyof typeof t]) : value,
                      })),
                    ],
                  },
                ]}
                onClear={() => {
                  setOrderQuery("");
                  setOrderStatus("all");
                }}
              />
              <div className="request-list">
                {filteredOrders.length ? (
                  <PaginatedList
                    items={filteredOrders}
                    resetKey={`${orderQuery}:${orderStatus}`}
                    label="Merchant order pages"
                    renderItem={(o) => (
                      <Card className="request-card" key={o.id}>
                        <div>
                          <StatusPill tone={o.status === "pending" ? "warning" : "neutral"}>
                            {o.status}
                          </StatusPill>
                          <h3>{o.customer_name}</h3>
                          {orderItems
                            .filter((item) => item.order_id === o.id)
                            .map((item) => (
                              <p key={item.item_id}>
                                {item.quantity} × {item.item_name} ·{" "}
                                {(item.line_total_minor / 100).toLocaleString()} MMK
                              </p>
                            ))}
                          <p>
                            {o.delivery_address} · {(o.total_minor / 100).toLocaleString()} MMK
                          </p>
                        </div>
                        <div className="request-actions">
                          {o.status === "pending" && (
                            <>
                              <button
                                className="button"
                                onClick={() => void next(o.id, "accepted")}
                              >
                                {t.accepted}
                              </button>
                              <button
                                className="button button--danger"
                                onClick={() => void next(o.id, "rejected")}
                              >
                                {t.rejected}
                              </button>
                            </>
                          )}
                          {o.status === "accepted" && (
                            <button className="button" onClick={() => void next(o.id, "preparing")}>
                              {t.preparing}
                            </button>
                          )}
                          {o.status === "preparing" && (
                            <button className="button" onClick={() => void next(o.id, "ready")}>
                              {t.ready}
                            </button>
                          )}
                          {o.status === "ready" && (
                            <button className="button" onClick={() => void next(o.id, "completed")}>
                              {t.completed}
                            </button>
                          )}
                        </div>
                      </Card>
                    )}
                  />
                ) : (
                  <RecordTable
                    items={filteredOrders}
                    rowKey={(order) => order.id}
                    label="Merchant orders"
                    empty={t.none}
                    columns={[
                      {
                        key: "customer",
                        label: "Customer",
                        render: (order) => order.customer_name,
                      },
                      { key: "status", label: "Status", render: (order) => order.status },
                      {
                        key: "total",
                        label: "Total",
                        render: (order) => (order.total_minor / 100).toLocaleString() + " MMK",
                      },
                    ]}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}
